/* iter135 · Studio-Editor für Tier-übergreifende Abo-Vorteile.
 * Zwei Bereichs-Sektionen (MP / STX) mit Rabatt-%-Slider, Early-Access-Stunden,
 * Toggle für Exklusiv-Sichtbarkeit und Tiptap-Beschreibung (mit Emoji-Support).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Store, Package, Save, Loader2, CheckCircle } from "lucide-react";
import api from "@/lib/api";
import RichTextEditor from "@/components/common/RichTextEditor";

const AREAS = [
  { key: "MARKETPLACE",   label: "Marketplace",   icon: Store },
  { key: "STILLTOUCHEDX", label: "stilltouchedX", icon: Package },
];

const EMPTY_AREA = {
  discount_pct: 0,
  early_access_hours: 0,
  allow_exclusive: false,
  description_html: "",
};

export default function StudioSubscriberBenefits() {
  const { t } = useTranslation();
  const [benefits, setBenefits] = useState({ MARKETPLACE: { ...EMPTY_AREA }, STILLTOUCHEDX: { ...EMPTY_AREA } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // area-key
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/studio/monetization/tier-benefits");
        const tb = data?.tier_benefits || {};
        setBenefits({
          MARKETPLACE: { ...EMPTY_AREA, ...(tb.MARKETPLACE || {}) },
          STILLTOUCHEDX: { ...EMPTY_AREA, ...(tb.STILLTOUCHEDX || {}) },
        });
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const update = (areaKey, field, value) => {
    setBenefits((prev) => ({ ...prev, [areaKey]: { ...prev[areaKey], [field]: value } }));
  };

  const save = async (areaKey) => {
    setSaving(areaKey);
    try {
      await api.put("/studio/monetization/tier-benefits", { [areaKey]: benefits[areaKey] });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <p className="text-sm text-[#999]">Lade …</p>;

  return (
    <div data-testid="studio-subscriber-benefits" className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-[#0A0A0A]">{t("sub_benefits.title")}</h2>
        <p className="text-xs text-[#666] mt-1">
          Diese Vorteile werden auf ALLE deine Listings im jeweiligen Bereich angewendet — sofern du sie nicht pro Listing überschreibst.
          Buyer sehen sie beim Abo-Kauf sowie direkt am Listing.
        </p>
      </div>

      {AREAS.map(({ key, label, icon: Icon }) => {
        const b = benefits[key];
        return (
          <section key={key} data-testid={`benefits-section-${key}`} className="rounded-2xl border border-[#E5E5E5] overflow-hidden bg-white">
            <header className="flex items-center gap-2 px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <Icon size={14} className="text-[#0A0A0A]" />
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-[#0A0A0A]">{label}</h3>
            </header>

            <div className="px-4 py-4 space-y-4">
              {/* Rabatt */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">{t("sub_benefits.discount_percent_label")}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0" max="50" step="1"
                    value={b.discount_pct}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const clamped = Math.max(0, Math.min(50, Number.isFinite(raw) ? raw : 0));
                      update(key, "discount_pct", clamped);
                    }}
                    data-testid={`benefit-${key}-discount-input`}
                    className="w-24 h-10 rounded-full border border-[#E5E5E5] px-3 text-sm font-bold text-center focus:border-[#0A0A0A] focus:outline-none"
                  />
                  <span className="text-sm font-bold text-[#0A0A0A]">%</span>
                  <span className="text-sm font-bold w-14 text-right ml-auto" data-testid={`benefit-${key}-discount-value`}>{b.discount_pct} %</span>
                </div>
                <p className="text-[10px] text-[#999] mt-1">0 % = kein Rabatt. Max 50 % (Admin-Bound).</p>
              </div>

              {/* Early Access */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">Early-Access-Fenster (Stunden vor Public-Release)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="0" max="168" step="1"
                    value={b.early_access_hours}
                    onChange={(e) => update(key, "early_access_hours", Math.max(0, Math.min(168, parseInt(e.target.value || 0, 10))))}
                    data-testid={`benefit-${key}-early-hours`}
                    className="w-24 h-10 px-4 rounded-full border border-[#E5E5E5] text-sm text-center"
                  />
                  <span className="text-xs text-[#666]">Stunden</span>
                </div>
                <p className="text-[10px] text-[#999] mt-1">{t("sub_benefits.early_access_hint")}</p>
              </div>

              {/* Exklusiv erlauben */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={b.allow_exclusive}
                    onChange={(e) => update(key, "allow_exclusive", e.target.checked)}
                    data-testid={`benefit-${key}-allow-exclusive`}
                    className="w-4 h-4 accent-[#0A0A0A]"
                  />
                  <div>
                    <p className="text-sm text-[#0A0A0A]">Exklusiv-Sichtbarkeit aktivieren</p>
                    <p className="text-[10px] text-[#999]">{t("sub_benefits.tier_gate_hint")}</p>
                  </div>
                </label>
              </div>

              {/* Beschreibung mit Tiptap-Editor */}
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#999] mb-1">Beschreibung deiner Vorteile (wird Buyern angezeigt)</label>
                <RichTextEditor
                  value={b.description_html || ""}
                  onChange={(html) => update(key, "description_html", html)}
                  placeholder={t("sub_benefits.benefits_placeholder")}
                  testId={`benefit-${key}-description`}
                />
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-[#F0F0F0]">
                <p className="text-[11px] text-[#999]">
                  {savedAt && <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle size={11} /> Gespeichert</span>}
                </p>
                <button
                  type="button"
                  onClick={() => save(key)}
                  disabled={saving === key}
                  data-testid={`benefit-${key}-save`}
                  className="h-10 px-5 rounded-full bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-2 disabled:opacity-40 hover:bg-[#222]"
                >
                  {saving === key ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Vorteile speichern
                </button>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
